/**
 * 네이버페이 주문 라인 조립 정책 — **부분 주문 금지**.
 *
 * ## 왜 필요한가
 *
 * "이 라인은 네이버페이로 못 판다"(= KRW 가격 행이 없거나 값이 이상하다)를 **두 표면이
 * 서로 다르게 처리**하고 있었다.
 *
 * | 표면 | 처리 |
 * | --- | --- |
 * | PDP 액세서리 | `continue` — **그 액세서리만 빼고 주문 진행** |
 * | 카트 | 하나라도 못 팔면 버튼 자체를 숨김 |
 * | PDP 메인 제품 | 버튼 자체를 숨김 |
 *
 * 즉 PDP 에서 **액세서리만 조용히 빠졌다.** 실제 시나리오: 새 배지가 admin 에 KRW 가격
 * 없이 등록된다 → KR 사용자가 PDP 에서 체크한다 → **구매 레일 총액에 포함되어 보인다**
 * → 네이버페이로 결제한다 → **주문에는 그 액세서리가 없다.** 사용자에게도 우리에게도
 * 에러가 없다. 통화별 가격 누락은 `docs/pricing-tiers.md` 가 기록한 반복 사고다.
 *
 * 이건 이 레포가 문서로 관리하는 **표시가 ≠ 청구액** 클래스다
 * (`docs/pitfalls.md` #29 · `docs/naverpay-pricing.md`).
 *
 * ## 정책은 하나다 — 팔 수 없는 라인이 있으면 **주문 자체를 막는다**
 *
 * 카트 쪽 판단이 옳다: 사용자가 고른 것 중 일부만 담아 결제하는 것보다, 버튼을 숨겨
 * 다른 결제수단으로 유도하는 편이 낫다. 빠진 걸 알아차릴 방법이 없기 때문이다.
 * `null` 을 "라인 하나 실패" 가 아니라 **"이 주문 전체 불가"** 로 전파한다.
 */

/**
 * 라인 전부를 변환하거나, **하나라도 실패하면 `null`**.
 *
 * ⚠️ 실패한 라인을 빼고 나머지를 돌려주지 않는다 — 그게 이 모듈이 없애려는 동작이다.
 * 호출처는 `null` 을 받으면 버튼을 렌더하지 않는다.
 *
 * `toLine` 이 `null` 을 돌려주는 것은 **"팔 수 없다"** 는 뜻이다. 애초에 청구 대상이
 * 아닌 항목(선택 해제·화면에서 사라진 액세서리 등)은 이 함수에 넣기 **전에** 걸러야
 * 한다 — 여기 넣으면 주문 전체가 막힌다.
 */
export function buildAllLinesOrNone<T, R>(
  chargeableLines: T[],
  toLine: (line: T) => R | null,
): R[] | null {
  const out: R[] = [];
  for (const line of chargeableLines) {
    const built = toLine(line);
    // ⚠️ `== null` (느슨한 비교)다. `null` 과 `undefined` 만 실패로 보고
    // **`0`·`""` 는 통과**시킨다 — 무료 라인(`basePrice: 0`)이 실제로 존재하고,
    // `!built` 로 판정하면 그것 때문에 주문이 통째로 막힌다(뮤테이션으로 확인).
    // `=== null` 만 보면 `undefined` 가 배열에 들어가 라인에 구멍이 생긴다.
    // 오늘은 `toLine` 시그니처가 `R | null` 이라 도달 불가지만, `R` 이 optional 을
    // 포함하면 뚫린다.
    if (built == null) return null;
    out.push(built);
  }
  return out;
}
